import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaHouse, FaUserGroup, FaBellConcierge, FaTrophy, FaGem,
  FaImages, FaBagShopping, FaPlane, FaPhone, FaEnvelope,
  FaStar, FaFacebookF, FaYoutube, FaInstagram, FaPaperPlane
} from 'react-icons/fa6';
import Logo2 from '../../assets/logos/azb.png';
import Tik from '../../assets/images/titok.png';

const Header = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Palette "True White & Sand Glassmorphism" enrichie d'un vert élégant (tapis de luxe)
  const colors = {
    text: '#3A312B',
    textSoft: '#7D6B5A',
    accent: '#D4B895',
    accentHover: '#C2A37D',
    // 🟢 Nouveaux verts
    greenPrimary: '#2A7F6E',
    greenLight: '#E8F5F2',
    greenHover: '#1E5F51',
    bgGlass: 'rgba(255, 255, 255, 0.5)',
    bgGlassScrolled: 'rgba(255, 255, 255, 0.82)',
    borderGlass: 'rgba(255, 255, 255, 0.7)',
    topBarBg: 'rgba(255, 255, 255, 0.25)',
    shadowSoft: '0 8px 32px rgba(212, 184, 149, 0.15)',
    shadowMedium: '0 12px 40px rgba(212, 184, 149, 0.25)',
  };

  const navLinks = [
    { path: '/', label: 'Accueil', Icon: FaHouse },
    { path: '/about', label: 'Qui sommes-nous', Icon: FaUserGroup },
    { path: '/Services', label: 'Nos Services', Icon: FaBellConcierge },
    { path: '/reference', label: 'Références', Icon: FaTrophy },
    { path: '/Fidelites', label: 'Fidélité', Icon: FaGem },
    { path: '/Galerie', label: 'Galerie', Icon: FaImages },
    { path: 'https://store.azyekos-services.com/', label: 'Store', Icon: FaBagShopping, external: true },
    { path: '/Azyekos-Lounges', label: 'Azyekos Lounges', Icon: FaPlane },
  ];

  // Gestion du scroll
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrolled(window.scrollY > 20);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const closeMobileMenu = useCallback(() => setMobileMenuOpen(false), []);

  useEffect(() => {
    closeMobileMenu();
  }, [location.pathname, closeMobileMenu]);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  useEffect(() => {
    const handleKey = (e) => e.key === 'Escape' && closeMobileMenu();
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [closeMobileMenu]);

  // Animations
  const overlayVariants = {
    hidden: { opacity: 0, backdropFilter: 'blur(0px)' },
    visible: { opacity: 1, backdropFilter: 'blur(30px)' },
    exit: { opacity: 0, backdropFilter: 'blur(0px)' },
  };

  const menuListVariants = {
    closed: { opacity: 0 },
    open: {
      opacity: 1,
      transition: { staggerChildren: 0.06, delayChildren: 0.1 },
    },
  };

  const menuItemVariants = {
    closed: { opacity: 0, y: 24 },
    open: {
      opacity: 1,
      y: 0,
      transition: { type: 'spring', stiffness: 260, damping: 24 },
    },
  };

  const hamburgerLine = (rotate, y, opacity = 1) => ({
    rotate,
    y,
    opacity,
    transition: { duration: 0.3, ease: 'easeInOut' },
  });

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, type: 'spring', stiffness: 80, damping: 20 }}
        style={{
          position: 'fixed',
          top: '12px',
          left: 0,
          width: '100%',
          zIndex: 1000,
          display: 'flex',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <motion.div
          animate={{
            background: scrolled ? colors.bgGlassScrolled : colors.bgGlass,
            boxShadow: scrolled ? colors.shadowMedium : colors.shadowSoft,
            borderRadius: scrolled ? '20px' : '24px',
          }}
          transition={{ duration: 0.4 }}
          style={{
            width: '100%',
            maxWidth: '1400px',
            margin: '0 20px',
            overflow: 'visible',
            border: `1px solid ${colors.borderGlass}`,
            borderTop: `1.5px solid rgba(255,255,255,0.95)`,
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            pointerEvents: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* TOP BAR DESKTOP */}
          <div
            className="top-bar-desktop"
            style={{
              background: colors.topBarBg,
              padding: '6px 24px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: `1px solid ${colors.borderGlass}`,
              fontSize: '0.75rem',
              fontWeight: 500,
              color: colors.textSoft,
            }}
          >
            <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {/* 🟢 Icône téléphone en vert */}
                <FaPhone style={{ color: colors.greenPrimary }} /> (+243) 835-593-568
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {/* 🟢 Icône email en vert */}
                <FaEnvelope style={{ color: colors.greenPrimary }} /> infos@azyekos-services.com
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {/* 🟢 Étoile en vert */}
                <FaStar style={{ color: colors.greenPrimary }} /> Confort & Élégance – Service Premium
              </div>
            </div>
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
              {[
                { Icon: FaFacebookF, href: 'https://web.facebook.com/azyekos' },
                { Icon: FaYoutube, href: 'https://www.youtube.com/@azyekos-Services' },
                { Icon: FaInstagram, href: 'https://www.instagram.com/azyekos_services_/' },
              ].map(({ Icon, href }) => (
                <motion.a
                  key={href}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  whileHover={{ y: -2, scale: 1.1, color: colors.greenPrimary }} // 🟢 survol vert
                  style={{ color: colors.textSoft, transition: 'color 0.2s' }}
                >
                  <Icon size={14} />
                </motion.a>
              ))}
              <motion.a
                href="https://www.tiktok.com/@azyekos.lounges"
                target="_blank"
                rel="noreferrer"
                whileHover={{ y: -2, scale: 1.15 }}
              >
                <img
                  src={Tik}
                  alt="TikTok"
                  style={{ width: '14px', height: '14px', filter: 'grayscale(100%) opacity(0.7)' }}
                />
              </motion.a>
            </div>
          </div>

          {/* MAIN NAVBAR */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: scrolled ? '10px 24px' : '16px 24px',
              transition: 'padding 0.4s ease',
              position: 'relative',
              zIndex: 1002,
            }}
          >
            <Link to="/" onClick={closeMobileMenu}>
              <motion.img
                src={Logo2}
                alt="Azyekos Logo"
                style={{ height: scrolled ? '40px' : '50px', transition: 'height 0.4s ease' }}
                whileHover={{ scale: 1.05, filter: 'drop-shadow(0 0 8px rgba(42,127,110,0.4))' }} // 🟢 lueur verte
                whileTap={{ scale: 0.95 }}
              />
            </Link>

            {/* NAV DESKTOP */}
            <nav className="desktop-nav" style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
              <ul style={{ listStyle: 'none', display: 'flex', gap: '24px', margin: 0, padding: 0 }}>
                {navLinks.map(({ path, label, external }) => {
                  const isActive = location.pathname === path;
                  return (
                    <li key={path}>
                      {external ? (
                        <a
                          href={path}
                          target="_blank"
                          rel="noreferrer"
                          className="nav-link"
                          style={{ '--hover-color': colors.greenPrimary }} // 🟢 variable pour CSS
                        >
                          {label}
                        </a>
                      ) : (
                        <Link
                          to={path}
                          className="nav-link"
                          style={{
                            color: isActive ? colors.greenPrimary : colors.text, // 🟢 actif en vert
                            fontWeight: isActive ? 600 : 500,
                          }}
                        >
                          {label}
                          {isActive && (
                            <motion.div
                              layoutId="underline"
                              style={{
                                position: 'absolute',
                                bottom: -4,
                                left: 0,
                                right: 0,
                                height: '2px',
                                borderRadius: '2px',
                                backgroundColor: colors.greenPrimary, // 🟢 soulignement vert
                              }}
                            />
                          )}
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>

              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link
                  to="/contact"
                  style={{
                    // 🟢 Dégradé beige → vert
                    background: `linear-gradient(135deg, ${colors.accent}, ${colors.greenPrimary})`,
                    color: '#fff',
                    padding: '10px 24px',
                    borderRadius: '30px',
                    textDecoration: 'none',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: `0 4px 15px rgba(42,127,110,0.4)`, // 🟢 ombre verte
                    border: '1px solid rgba(255,255,255,0.4)',
                  }}
                >
                  <FaPaperPlane size={12} /> Contact
                </Link>
              </motion.div>
            </nav>

            {/* BOUTON HAMBURGER MOBILE */}
            <button
              className="mobile-toggle"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
              style={{
                display: 'none',
                background: mobileMenuOpen ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.2)',
                border: `1px solid ${colors.borderGlass}`,
                borderRadius: '50%',
                cursor: 'pointer',
                width: '44px',
                height: '44px',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '5px',
                zIndex: 1005,
                boxShadow: mobileMenuOpen ? '0 4px 12px rgba(0,0,0,0.05)' : 'none',
                backdropFilter: 'blur(8px)',
                transition: 'all 0.3s ease',
              }}
            >
              <motion.span
                animate={mobileMenuOpen ? hamburgerLine(45, 7) : hamburgerLine(0, 0)}
                style={{ display: 'block', width: 22, height: 2, borderRadius: 2, background: colors.text }}
              />
              <motion.span
                animate={mobileMenuOpen ? hamburgerLine(0, 0, 0) : hamburgerLine(0, 0, 1)}
                style={{ display: 'block', width: 22, height: 2, borderRadius: 2, background: colors.text }}
              />
              <motion.span
                animate={mobileMenuOpen ? hamburgerLine(-45, -7) : hamburgerLine(0, 0)}
                style={{ display: 'block', width: 22, height: 2, borderRadius: 2, background: colors.text }}
              />
            </button>
          </div>
        </motion.div>
      </motion.header>

      {/* OVERLAY MENU MOBILE */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            key="mobile-overlay"
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.35, ease: 'easeInOut' }}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 999,
              background: 'rgba(255, 255, 255, 0.7)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '20px',
            }}
            onClick={closeMobileMenu}
          >
            <motion.nav
              variants={menuListVariants}
              initial="closed"
              animate="open"
              exit="closed"
              style={{
                width: '100%',
                maxWidth: '360px',
                background: 'rgba(255, 255, 255, 0.75)',
                backdropFilter: 'blur(25px)',
                WebkitBackdropFilter: 'blur(25px)',
                borderRadius: '28px',
                padding: '32px 24px',
                boxShadow: '0 25px 50px -12px rgba(0,0,0,0.1), 0 0 0 1px rgba(255,255,255,0.8)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {navLinks.map(({ path, label, Icon, external }) => {
                  const isActive = location.pathname === path;
                  const itemContent = (
                    <>
                      <Icon size={18} style={{ color: isActive ? colors.greenPrimary : colors.textSoft }} />
                      <span style={{ fontWeight: isActive ? 600 : 500 }}>{label}</span>
                    </>
                  );

                  return (
                    <motion.li key={path} variants={menuItemVariants} whileTap={{ scale: 0.97 }}>
                      {external ? (
                        <a
                          href={path}
                          target="_blank"
                          rel="noreferrer"
                          className="mobile-link"
                          onClick={closeMobileMenu}
                        >
                          {itemContent}
                        </a>
                      ) : (
                        <Link
                          to={path}
                          className="mobile-link"
                          style={{
                            background: isActive ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.5)',
                            border: `1px solid ${
                              isActive ? colors.greenPrimary : 'rgba(255,255,255,0.6)' // 🟢 bordure verte si actif
                            }`,
                            color: isActive ? colors.greenPrimary : colors.text, // 🟢 texte vert si actif
                            boxShadow: isActive
                              ? '0 8px 20px rgba(42,127,110,0.15)'
                              : '0 4px 12px rgba(0,0,0,0.03)',
                          }}
                          onClick={closeMobileMenu}
                        >
                          {itemContent}
                        </Link>
                      )}
                    </motion.li>
                  );
                })}

                <motion.li
                  variants={menuItemVariants}
                  whileTap={{ scale: 0.97 }}
                  style={{ marginTop: '8px' }}
                >
                  <Link
                    to="/contact"
                    onClick={closeMobileMenu}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '10px',
                      padding: '16px',
                      borderRadius: '16px',
                      background: `linear-gradient(135deg, ${colors.accent}, ${colors.greenPrimary})`, // 🟢 dégradé vert
                      color: '#fff',
                      textDecoration: 'none',
                      fontWeight: 600,
                      fontSize: '1.05rem',
                      boxShadow: '0 10px 25px rgba(42,127,110,0.4)',
                      border: '1px solid rgba(255,255,255,0.3)',
                    }}
                  >
                    <FaEnvelope size={16} /> Nous contacter
                  </Link>
                </motion.li>
              </ul>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>

      {/* STYLES RESPONSIVES */}
      <style>{`
        .nav-link {
          display: flex;
          align-items: center;
          gap: 6px;
          position: relative;
          text-decoration: none;
          font-size: 0.9rem;
          font-weight: 500;
          color: ${colors.text};
          transition: color 0.3s ease;
          padding: 5px 0;
        }
        .nav-link:hover {
          color: var(--hover-color, ${colors.greenPrimary}); /* 🟢 survol vert */
        }
        .mobile-link {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 16px 20px;
          border-radius: 16px;
          text-decoration: none;
          font-size: 1.05rem;
          background: rgba(255,255,255,0.5);
          border: 1px solid rgba(255,255,255,0.7);
          color: ${colors.text};
          backdrop-filter: blur(5px);
          transition: all 0.2s ease;
        }
        .mobile-link:hover {
          background: rgba(255,255,255,0.8);
          transform: translateX(4px);
          border-color: ${colors.greenPrimary}; /* 🟢 bordure verte au survol */
        }
        @media (max-width: 1024px) {
          .top-bar-desktop { display: none !important; }
          .desktop-nav { display: none !important; }
          .mobile-toggle { display: flex !important; }
        }
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
            scroll-behavior: auto !important;
          }
        }
      `}</style>
    </>
  );
};

export default Header;